import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  createOrganization, getMyOrganization, inviteTeamMember,
  getInvitations, acceptInvitation, updateMemberRole,
  removeMember, getOrgActivity
} from '../lib/api';
import { toast } from 'sonner';
import {
  Building2, Users, UserPlus, Mail, Shield, Loader2,
  Crown, UserCog, User, Trash2, Check, X, Activity
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const Team = () => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  
  // Form states
  const [orgName, setOrgName] = useState('');
  const [businessType, setBusinessType] = useState('general');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, invitesRes] = await Promise.all([
        getMyOrganization(),
        getInvitations()
      ]);
      setOrganization(orgRes.data.organization);
      setInvitations(invitesRes.data.invitations || []);
      
      if (orgRes.data.organization) {
        const activityRes = await getOrgActivity(20);
        setActivities(activityRes.data.activities || []);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Please enter an organization name');
      return;
    }
    setCreating(true);
    try {
      await createOrganization({ name: orgName, business_type: businessType });
      toast.success('Organization created');
      setShowCreateOrgDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setCreating(true);
    try {
      await inviteTeamMember(inviteEmail, inviteRole);
      toast.success('Invitation sent');
      setShowInviteDialog(false);
      setInviteEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setCreating(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await acceptInvitation(invitationId);
      toast.success('Invitation accepted');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept invitation');
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await updateMemberRole(memberId, newRole);
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await removeMember(memberId);
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-amber-500" />;
      case 'manager': return <UserCog className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-amber-500/20 text-amber-600 border-0',
      manager: 'bg-blue-500/20 text-blue-600 border-0',
      member: 'bg-slate-500/20 text-slate-600 border-0'
    };
    return <Badge className={colors[role] || colors.member}>{role}</Badge>;
  };

  const isOwner = organization?.owner_id === user?.id;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="team-container"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Team</h1>
            <p className="text-muted-foreground">Manage your organization and team members</p>
          </div>
        </div>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <Card className="border-emerald-500/50 bg-emerald-500/5">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-emerald-600">
                <Mail className="w-5 h-5" />
                Pending Invitations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {invitations.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between p-3 rounded-xl bg-background">
                  <div>
                    <p className="font-medium">{invite.organization_name}</p>
                    <p className="text-sm text-muted-foreground">Invited as {invite.role}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleAcceptInvitation(invite.id)} className="rounded-lg">
                      <Check className="w-4 h-4 mr-1" /> Accept
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {!organization ? (
          /* No Organization State */
          <Card>
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h2 className="text-xl font-heading font-semibold mb-2">No Organization Yet</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Create an organization to manage team members, set spending limits, and share wallets
              </p>
              <Button onClick={() => setShowCreateOrgDialog(true)} className="rounded-xl" data-testid="create-org-btn">
                <Building2 className="w-4 h-4 mr-2" />
                Create Organization
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Organization Dashboard */
          <>
            {/* Organization Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-heading font-bold">{organization.name}</h2>
                    <p className="text-muted-foreground capitalize">{organization.business_type} Business</p>
                  </div>
                  {isOwner && (
                    <Button onClick={() => setShowInviteDialog(true)} className="rounded-xl" data-testid="invite-btn">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite Member
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="members" className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-12 rounded-xl">
                <TabsTrigger value="members" className="rounded-lg" data-testid="members-tab">
                  <Users className="w-4 h-4 mr-2" />
                  Members ({organization.members?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="activity" className="rounded-lg" data-testid="activity-tab">
                  <Activity className="w-4 h-4 mr-2" />
                  Activity
                </TabsTrigger>
              </TabsList>

              <TabsContent value="members" className="space-y-4 mt-6">
                {organization.members?.map((member) => (
                  <Card key={member.user_id} data-testid={`member-${member.user_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-muted">
                              {member.email?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{member.email}</p>
                              {member.user_id === organization.owner_id && (
                                <Badge className="bg-amber-500/20 text-amber-600 border-0 text-xs">Owner</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              {getRoleIcon(member.role)}
                              <span className="capitalize">{member.role}</span>
                              <span className="mx-1">•</span>
                              <span>Joined {new Date(member.joined_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        {isOwner && member.user_id !== user?.id && (
                          <div className="flex items-center gap-2">
                            <Select
                              value={member.role}
                              onValueChange={(v) => handleUpdateRole(member.user_id, v)}
                            >
                              <SelectTrigger className="w-28 rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="manager">Manager</SelectItem>
                                <SelectItem value="member">Member</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => handleRemoveMember(member.user_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="activity" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Recent Activity</CardTitle>
                    <CardDescription>Team member transactions and actions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {activities.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No activity yet
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {activities.map((activity) => (
                          <div key={activity.id} className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                            <div>
                              <p className="font-medium">{activity.merchant}</p>
                              <p className="text-sm text-muted-foreground">{activity.category}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-semibold">${activity.amount?.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(activity.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </motion.div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>Set up your business account to manage team members</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                placeholder="Acme Inc."
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="rounded-xl"
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Type</Label>
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="tech">Technology</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="services">Services</SelectItem>
                  <SelectItem value="nonprofit">Non-Profit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateOrgDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleCreateOrg} disabled={creating} className="rounded-xl" data-testid="create-org-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Send an invitation to join your organization</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="rounded-xl"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin - Full access</SelectItem>
                  <SelectItem value="manager">Manager - Can manage members</SelectItem>
                  <SelectItem value="member">Member - Basic access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleInvite} disabled={creating} className="rounded-xl" data-testid="send-invite-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Team;
